import { HttpClient } from '@adapters/HttpClient';
import { AxiosRequestConfig } from 'axios';
import { Filters } from '../models/Filters';
import { OrdersWithoutWeightPage } from '../models/Order';

// Mapeo de columnas del frontend (colId) a columnas del backend (export API)
const COLUMN_MAPPING: Record<string, string> = {
  id: 'id',
  ref: 'ref',
  external_id: 'external_id',
  courier: 'courier',
  shop: 'shop',
  team: 'team',
  processed_at: 'processed_at',
  last_shipment_update: 'last_shipment_update',
  shipment_status: 'shipment_status',
  tracking_number: 'tracking_number',
  total: 'total',
  created_at: 'creation_date',
  is_migrated: 'is_migrated',
};

// Columnas por defecto para exportación
const DEFAULT_EXPORT_COLUMNS = [
  'id',
  'ref',
  'shop',
  'courier',
  'processed_at',
  'last_shipment_update',
  'shipment_status',
  'tracking_number',
  'total',
  'creation_date',
];

// Función para mapear columnas del frontend al backend
function mapVisibleColumnsToExport(visibleColumns: string[]): string[] {
  if (!visibleColumns || visibleColumns.length === 0) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  const mappedColumns = new Set<string>();
  // Siempre incluir 'id' como primera columna
  mappedColumns.add('id');

  visibleColumns.forEach((frontendCol) => {
    const backendCol = COLUMN_MAPPING[frontendCol];
    if (backendCol) {
      mappedColumns.add(backendCol);
    }
  });

  // Si no se mapeó ninguna columna válida, usar las por defecto
  if (mappedColumns.size <= 1) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  return Array.from(mappedColumns);
}

// 🎯 Función para construir parámetros de advanced_filters
function buildAdvancedFiltersParams(advancedFilters: {
  filters: Array<{
    column: string;
    operator: string;
    value: string | string[] | number | number[] | boolean | null;
  }>;
  logic: string;
}) {
  const params: Record<string, any> = {};

  if (advancedFilters.filters && Array.isArray(advancedFilters.filters)) {
    advancedFilters.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;

      // 🎯 Manejar valores de array para operadores 'in' y 'not_in'
      if (f.operator === 'in' || f.operator === 'not_in') {
        // Si el valor es un array, enviar cada elemento
        if (Array.isArray(f.value)) {
          f.value.forEach((val, j) => {
            params[`advanced_filters[filters][${i}][value][${j}]`] = val;
          });
        } else if (f.value !== null && f.value !== undefined) {
          // Si es un string o número, convertirlo a array con un elemento
          params[`advanced_filters[filters][${i}][value][0]`] = f.value;
        }
      } else {
        // Para otros operadores, enviar el valor directamente (puede ser null)
        if (f.value !== null && f.value !== undefined) {
          params[`advanced_filters[filters][${i}][value]`] = Array.isArray(f.value) ? f.value[0] : f.value;
        }
      }
    });
  }

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
}

// 🎯 Función para construir query string de exportación
function buildExportQueryString(filters: Filters): string {
  const params: Record<string, any> = {};

  // Agregar sorting si está presente
  if (filters.sorting) {
    params['sorting[column]'] = filters.sorting.column;
    params['sorting[direction]'] = filters.sorting.direction;
  }

  // Agregar filtros avanzados si existen
  if (filters.advanced_filters && filters.advanced_filters.filters?.length) {
    const advancedParams = buildAdvancedFiltersParams(filters.advanced_filters);
    Object.assign(params, advancedParams);
  }

  // Construir query string
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

export interface ExportOrdersWithoutWeightResponse {
  url_file: string;
}

export interface ExportOrdersWithoutWeightBody {
  columns: string[];
}

export class OrdersWithoutWeightApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getOrdersWithoutWeightPaginated(filters: Filters = {}): Promise<OrdersWithoutWeightPage> {
    const { page = 1, per_page = 15, sorting, advanced_filters } = filters;

    // 🎯 Construir parámetros
    const params: Record<string, any> = {
      page,
      per_page,
    };

    // 🎯 Agregar sorting si está presente
    if (sorting) {
      params['sorting[column]'] = sorting.column;
      params['sorting[direction]'] = sorting.direction;
    }

    // 🎯 Agregar filtros avanzados solo si el usuario los proporciona explícitamente
    if (advanced_filters && advanced_filters.filters?.length) {
      const advancedParams = buildAdvancedFiltersParams(advanced_filters);
      Object.assign(params, advancedParams);
    }

    // 🎯 Serializar parámetros con corchetes escapados correctamente
    const config: AxiosRequestConfig = {
      paramsSerializer: (params) => {
        const parts: string[] = [];
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (value !== null && value !== undefined) {
            // Escapar corchetes en las claves
            const encodedKey = key.replace(/\[/g, '%5B').replace(/\]/g, '%5D');
            parts.push(`${encodedKey}=${encodeURIComponent(value)}`);
          }
        });
        return parts.join('&');
      },
    };

    const response = await this.httpClient.get<any>('/orders-without-weight', params, config);

    return {
      orders: response.data || [],
      nextPage: response.meta?.currentPage < response.meta?.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta ? response.meta.currentPage < response.meta.lastPage : false,
      total: response.meta?.total ?? 0,
    };
  }

  async exportOrdersWithoutWeight(
    filters: Filters,
    visibleColumns?: string[],
  ): Promise<ExportOrdersWithoutWeightResponse> {
    const queryString = buildExportQueryString(filters);
    const url = `/orders-without-weight/export${queryString ? `?${queryString}` : ''}`;
    const columns = mapVisibleColumnsToExport(visibleColumns || []);
    const body: ExportOrdersWithoutWeightBody = { columns };
    return await this.httpClient.post(url, body);
  }
}
